import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import QRPosterModal from "@/components/shared/QRPosterModal";
import OnSiteNowBoard from "@/components/shared/OnSiteNowBoard";
import { HeroFlowTrigger } from "@/components/shared/HeroFlowRunner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Truck, Lock, CheckCircle2, Clock, AlertTriangle, ArrowRight, DollarSign,
  Send, Phone, Mail, Sparkles, Calendar, MessageSquare, Link2, QrCode,
  Zap, Timer, ShieldCheck, TrendingUp, Target, Users, HardHat,
  FileText, Camera, XCircle, Smartphone, ExternalLink, ClipboardCheck, Eye
} from "lucide-react";

const stateConfig = {
  hold: { label: "On Hold", bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500", icon: Lock, headerBg: "bg-red-500" },
  waiting: { label: "Waiting", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500", icon: Clock, headerBg: "bg-amber-500" },
  ready: { label: "Ready", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500", icon: CheckCircle2, headerBg: "bg-emerald-500" },
  'link-sent': { label: "Link Sent", bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", dot: "bg-violet-500", icon: Link2, headerBg: "bg-violet-500" },
  'checked-in': { label: "On Site", bg: "bg-teal-50", border: "border-teal-200", text: "text-teal-700", dot: "bg-teal-500", icon: HardHat, headerBg: "bg-teal-600" },
  'cannot-start': { label: "Cannot Start", bg: "bg-red-50", border: "border-red-300", text: "text-red-700", dot: "bg-red-600", icon: XCircle, headerBg: "bg-red-600" },
  'signed-off': { label: "Signed Off", bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500", icon: ClipboardCheck, headerBg: "bg-blue-600" },
  released: { label: "Signed Off", bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500", icon: ClipboardCheck, headerBg: "bg-blue-600" },
};

const delayCosts = { 'Concreter': [12000, 18000], 'Framer': [8000, 14000], 'Plumber (rough-in)': [4500, 7000], 'Electrician (rough-in)': [3500, 6000], 'Steel Fabricator': [15000, 22000], 'Landscaper': [5000, 9000], 'Painter (touch-up)': [2000, 3500], 'Waterproofer': [6000, 10000] };

function TradeCard({ trade, blockerDetails, onSelect, onRelease, onGeneratePass }) {
  const cfg = stateConfig[trade.status] || stateConfig.waiting;
  const isReleasable = trade.status === 'ready' && trade.passStatus === 'none';
  const isReady = trade.status === 'ready';
  const isHeld = trade.status === 'hold' || trade.status === 'cannot-start';
  const costs = delayCosts[trade.trade] || [3000, 8000];

  return (
    <div onClick={() => onSelect(trade)} data-testid={`trade-card-${trade.id}`}
      className={`group relative rounded-xl border ${cfg.border} ${cfg.bg} cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 overflow-hidden`}>
      <div className={`h-1 ${cfg.headerBg}`} />
      <div className="p-3.5">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${cfg.border} ${isHeld ? 'bg-red-100' : isReady ? 'bg-emerald-100' : 'bg-white/80'}`}>
              <Truck className={`w-4 h-4 ${cfg.text}`} />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900 leading-tight">{trade.trade}</p>
              <p className="text-[10px] text-slate-500">{trade.company}</p>
            </div>
          </div>
          <Badge className={`text-[9px] px-2 ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} mr-1 inline-block`} />{cfg.label}
          </Badge>
        </div>

        <p className="text-[11px] text-slate-600 mb-2.5">{trade.project} · {trade.stage}</p>

        {/* Crew info for checked-in */}
        {(trade.status === 'checked-in') && trade.crewCount && (
          <div className="flex items-center gap-3 mb-2.5 p-2 bg-teal-100/50 rounded-lg border border-teal-200/60">
            <span className="text-[10px] text-teal-700 font-medium flex items-center gap-1"><Users className="w-3 h-3" />{trade.crewCount} crew</span>
            <span className="text-[10px] text-teal-600 flex items-center gap-1"><Clock className="w-3 h-3" />Since {trade.checkInTime}</span>
          </div>
        )}

        {/* Cannot Start reason */}
        {trade.status === 'cannot-start' && trade.cannotStartReason && (
          <div className="p-2 bg-red-100/60 rounded-lg border border-red-200/80 mb-2.5">
            <p className="text-[10px] text-red-700 font-medium flex items-start gap-1.5">
              <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />{trade.cannotStartReason}
            </p>
          </div>
        )}

        {/* Blockers for held trades */}
        {trade.status === 'hold' && blockerDetails.length > 0 && (
          <div className="space-y-1.5 mb-2.5">
            {blockerDetails.slice(0, 2).map((b, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-white/70 rounded-lg border border-red-100/80">
                <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" />
                <span className="text-[10px] text-red-700 flex-1 truncate font-medium">{b.title}</span>
                <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded-full ${b.severity === 'critical' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>{b.severity}</span>
              </div>
            ))}
          </div>
        )}

        {/* Cost exposure for held trades */}
        {trade.status === 'hold' && (
          <div className="flex items-center justify-between pt-2 border-t border-red-100/60">
            <span className="text-[10px] font-bold text-red-600 flex items-center gap-1"><DollarSign className="w-3 h-3" />${(costs[0]/1000).toFixed(0)}k–${(costs[1]/1000).toFixed(0)}k at risk</span>
            <span className="text-[10px] text-red-600 font-medium flex items-center gap-1"><Timer className="w-3 h-3" />{trade.blockersRemaining} to clear</span>
          </div>
        )}

        {/* Action: Generate Pass for ready trades */}
        {isReady && trade.passStatus === 'none' && (
          <Button size="sm" className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 font-semibold"
            onClick={(e) => { e.stopPropagation(); onGeneratePass(trade.id); }} data-testid={`generate-pass-btn-${trade.id}`}>
            <Smartphone className="w-3.5 h-3.5 mr-1.5" /> Generate SiteReady Pass
          </Button>
        )}
        {isReady && trade.passStatus === 'generated' && (
          <Button size="sm" className="w-full mt-2 bg-violet-600 hover:bg-violet-700 text-white text-xs h-8 font-semibold"
            onClick={(e) => { e.stopPropagation(); onRelease(trade.id); }} data-testid={`send-link-btn-${trade.id}`}>
            <Send className="w-3.5 h-3.5 mr-1.5" /> Send Link via {trade.channel}
          </Button>
        )}

        {/* Signed off indicator */}
        {(trade.status === 'signed-off' || trade.status === 'released') && (
          <div className="mt-2 flex items-center gap-1.5 text-[10px] text-blue-600 font-medium bg-blue-100/50 rounded-lg px-2.5 py-1.5">
            <ClipboardCheck className="w-3 h-3" /> Signed off {trade.signedOffDate || trade.earliestRelease || ''}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TradeRelease() {
  const {
    trades, blockers, workspace, calendarEvents, projects, conditions, tradeReadiness, workProofs,
    releaseTrade, sendChase, logAction, generateTradePass, sendTradePassLink,
    checkInTrade, markCannotStart, resolveCannotStart, submitWorkProof, approveWorkProof, signOffTrade
  } = useDemoState();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [qrTrade, setQrTrade] = useState(null);
  const [drawerTab, setDrawerTab] = useState('overview');

  const tradeData = useMemo(() => trades.map(t => {
    const tradeBlockers = (t.blockerIds || []).map(bid => blockers.find(b => b.id === bid)).filter(Boolean);
    const readiness = tradeReadiness.find(r => r.tradeId === t.id);
    const tWorkProofs = workProofs.filter(w => w.tradeId === t.id);
    return { ...t, _blockers: tradeBlockers, _readiness: readiness, _workProofs: tWorkProofs };
  }), [trades, blockers, tradeReadiness, workProofs]);

  // Board columns mapping
  const columns = [
    { id: 'hold', label: 'On Hold', trades: tradeData.filter(t => t.status === 'hold' || t.status === 'cannot-start') },
    { id: 'ready', label: 'Ready to Release', trades: tradeData.filter(t => t.status === 'ready' || t.status === 'waiting') },
    { id: 'link-sent', label: 'Link Sent', trades: tradeData.filter(t => t.status === 'link-sent') },
    { id: 'checked-in', label: 'On Site', trades: tradeData.filter(t => t.status === 'checked-in') },
    { id: 'signed-off', label: 'Signed Off', trades: tradeData.filter(t => t.status === 'signed-off' || t.status === 'released') },
  ];

  const totalDelayExposure = useMemo(() => {
    let min = 0, max = 0;
    tradeData.filter(t => t.status === 'hold' || t.status === 'cannot-start').forEach(t => {
      const costs = delayCosts[t.trade] || [3000, 8000];
      min += costs[0]; max += costs[1];
    });
    return { min, max };
  }, [tradeData]);

  const suggestions = useMemo(() => {
    const s = [];
    const held = tradeData.filter(t => t.status === 'hold');
    const cannotStart = tradeData.filter(t => t.status === 'cannot-start');
    const ready = tradeData.filter(t => t.status === 'ready');
    const onSite = tradeData.filter(t => t.status === 'checked-in');
    if (cannotStart.length > 0) cannotStart.forEach(t => s.push({ type: 'warning', text: `${t.trade} on site but CANNOT START: "${t.cannotStartReason}". Resolve immediately.` }));
    if (held.length > 0) s.push({ type: 'warning', text: `${held.length} trade(s) on hold. Delay exposure: $${(totalDelayExposure.min/1000).toFixed(0)}k–$${(totalDelayExposure.max/1000).toFixed(0)}k.` });
    ready.forEach(t => s.push({ type: 'success', text: `${t.trade} (${t.company}) is ready — generate SiteReady Trade Pass.` }));
    if (onSite.length > 0) s.push({ type: 'info', text: `${onSite.length} trade(s) on site. Total crew: ${onSite.reduce((a, t) => a + (t.crewCount || 0), 0)}.` });
    return s;
  }, [tradeData, totalDelayExposure]);

  const handleGeneratePass = (tradeId) => {
    generateTradePass(tradeId);
    toast.success('SiteReady Trade Pass generated', { description: 'Ready to send link to trade partner.' });
  };

  const handleSendLink = (tradeId) => {
    sendTradePassLink(tradeId);
    toast.success('SiteReady link sent', { description: 'Trade partner notified via WhatsApp.' });
  };

  const handleChase = (blockerId) => {
    sendChase(blockerId, 'email');
    toast.success('Chase sent', { description: 'Follow-up dispatched.' });
  };

  const handleSignOff = (tradeId) => {
    signOffTrade(tradeId);
    toast.success('Trade signed off', { description: 'Work completed and recorded.' });
    setSelected(null);
  };

  const handleResolveCannotStart = (tradeId) => {
    resolveCannotStart(tradeId);
    toast.success('Cannot Start resolved', { description: 'Trade can now proceed.' });
  };

  const sel = selected ? tradeData.find(t => t.id === selected.id) : null;
  const selBlockers = sel?._blockers || [];
  const selReadiness = sel?._readiness;
  const selWorkProofs = sel?._workProofs || [];
  const selCalEvents = sel ? calendarEvents.filter(e => e.trade === sel.trade || (sel.linkedCalendarId && e.id === sel.linkedCalendarId)) : [];
  const selProject = sel ? projects.find(p => p.id === sel.projectId) : null;

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="trade-release-page">
        {/* Pipeline summary */}
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="trade-pipeline-summary">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-900">Trade Coordination Pipeline</span>
              <Badge className="text-[9px] bg-slate-100 text-slate-600">{trades.length} trades</Badge>
            </div>
            <div className="flex items-center gap-2">
              <HeroFlowTrigger />
              {totalDelayExposure.max > 0 && (
                <div className="flex items-center gap-1.5 bg-red-50 border border-red-100 rounded-lg px-3 py-1.5">
                  <DollarSign className="w-3.5 h-3.5 text-red-500" />
                  <span className="text-xs font-bold text-red-700">${(totalDelayExposure.min/1000).toFixed(0)}k–${(totalDelayExposure.max/1000).toFixed(0)}k delay exposure</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {columns.map((col, i) => {
              const cfg = stateConfig[col.id] || stateConfig.hold;
              const pct = trades.length > 0 ? Math.max((col.trades.length / trades.length) * 100, 10) : 20;
              return (
                <div key={col.id} className="flex items-center gap-1.5" style={{ flex: pct }}>
                  <div className={`flex-1 rounded-lg ${cfg.bg} border ${cfg.border} px-2.5 py-2 text-center transition-all`}>
                    <p className={`text-xl font-heading font-bold ${cfg.text}`}>{col.trades.length}</p>
                    <p className="text-[9px] font-medium text-slate-500">{col.label}</p>
                  </div>
                  {i < columns.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Trade Intelligence */}
        {suggestions.length > 0 && (
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-4 text-white" data-testid="trade-ai-suggestions">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-md bg-blue-500/20 flex items-center justify-center"><Sparkles className="w-3.5 h-3.5 text-blue-400" /></div>
              <span className="text-xs font-bold text-white">Trade Intelligence</span>
              <span className="text-[9px] text-slate-400 ml-1">AI-generated insights</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {suggestions.slice(0, 4).map((s, i) => (
                <div key={i} className={`flex items-start gap-2 p-2.5 rounded-lg border ${s.type === 'warning' ? 'bg-red-500/10 border-red-500/20' : s.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-blue-500/10 border-blue-500/20'}`}>
                  {s.type === 'warning' ? <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" /> : s.type === 'success' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" /> : <Sparkles className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />}
                  <span className="text-[11px] text-slate-200 leading-relaxed">{s.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* On-Site Now Board */}
        <OnSiteNowBoard />

        {/* Board columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="trade-board">
          {columns.map(col => {
            const cfg = stateConfig[col.id] || stateConfig.hold;
            return (
              <div key={col.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden min-h-[180px]" data-testid={`column-${col.id}`}>
                <div className={`${cfg.headerBg} px-3 py-2 flex items-center justify-between`}>
                  <span className="text-[10px] font-bold text-white uppercase tracking-wider">{col.label}</span>
                  <span className="text-[10px] font-bold text-white/80 bg-white/20 rounded-full w-5 h-5 flex items-center justify-center">{col.trades.length}</span>
                </div>
                <div className="p-2.5 space-y-2.5">
                  {col.trades.map(t => (
                    <TradeCard key={t.id} trade={t} blockerDetails={t._blockers} onSelect={setSelected} onRelease={handleSendLink} onGeneratePass={handleGeneratePass} />
                  ))}
                  {col.trades.length === 0 && (
                    <div className="text-center py-6">
                      <cfg.icon className="w-5 h-5 text-slate-200 mx-auto mb-1.5" />
                      <p className="text-[10px] text-slate-400">No trades</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail drawer */}
        <Sheet open={!!selected} onOpenChange={() => setSelected(null)}>
          <SheetContent className="sm:max-w-xl overflow-y-auto" data-testid="trade-detail-drawer">
            {sel && (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${(stateConfig[sel.status] || stateConfig.waiting).bg}`}>
                      <Truck className={`w-4 h-4 ${(stateConfig[sel.status] || stateConfig.waiting).text}`} />
                    </div>
                    {sel.trade}
                  </SheetTitle>
                  <SheetDescription>{sel.company} · {sel.contact}</SheetDescription>
                </SheetHeader>

                <Tabs value={drawerTab} onValueChange={setDrawerTab} className="mt-4">
                  <TabsList className="w-full grid grid-cols-5 h-8">
                    <TabsTrigger value="overview" className="text-[10px]">Overview</TabsTrigger>
                    <TabsTrigger value="readiness" className="text-[10px]">Readiness</TabsTrigger>
                    <TabsTrigger value="pass" className="text-[10px]">Trade Pass</TabsTrigger>
                    <TabsTrigger value="attendance" className="text-[10px]">Attendance</TabsTrigger>
                    <TabsTrigger value="proof" className="text-[10px]">Work Proof</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-[10px] text-slate-400 font-semibold uppercase mb-1">Status</p>
                        <Badge className={`${(stateConfig[sel.status] || stateConfig.waiting).bg} ${(stateConfig[sel.status] || stateConfig.waiting).text} border ${(stateConfig[sel.status] || stateConfig.waiting).border} text-xs`}>
                          {(stateConfig[sel.status] || stateConfig.waiting).label}
                        </Badge>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-[10px] text-slate-400 font-semibold uppercase mb-1">Project</p>
                        <p className="text-xs font-medium text-slate-700">{sel.project}</p>
                        <p className="text-[10px] text-slate-500">{sel.stage}</p>
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] text-slate-400 font-semibold uppercase mb-2">Contact</p>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-xs font-semibold text-slate-600">{sel.contact?.split(' ').map(n => n[0]).join('')}</div>
                        <div className="flex-1">
                          <p className="text-xs font-medium text-slate-700">{sel.contact}</p>
                          <p className="text-[10px] text-slate-500">{sel.phone} · {sel.channel}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => toast.info('Calling...')}><Phone className="w-3 h-3" /></Button>
                          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => toast.info('WhatsApp opened')}><MessageSquare className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    </div>
                    {selBlockers.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-red-500" /> Blocking ({selBlockers.length})</p>
                        {selBlockers.map(b => (
                          <div key={b.id} className="p-3 bg-red-50 border border-red-100 rounded-lg mb-2">
                            <div className="flex items-start justify-between mb-1"><p className="text-xs font-medium text-red-800">{b.title}</p><Badge className="text-[9px] bg-red-100 text-red-700">{b.severity}</Badge></div>
                            <p className="text-[10px] text-red-600 mb-2">Owner: {b.owner} · Chased {b.chaseCount}x</p>
                            <Button size="sm" variant="outline" className="text-[10px] h-6 border-red-200 text-red-600" onClick={() => handleChase(b.id)}><Send className="w-2.5 h-2.5 mr-1" /> Chase</Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {sel.status === 'cannot-start' && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs font-bold text-red-800 mb-1 flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5" /> Cannot Start</p>
                        <p className="text-xs text-red-700 mb-2">{sel.cannotStartReason}</p>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => handleResolveCannotStart(sel.id)} data-testid="resolve-cannot-start-btn"><CheckCircle2 className="w-3 h-3 mr-1" /> Resolve</Button>
                      </div>
                    )}
                    <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                      <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-indigo-600" /><span className="text-xs font-semibold text-indigo-700">Next Best Action</span></div>
                      {sel.status === 'hold' && <p className="text-xs text-indigo-700">Clear blockers to move to Ready. Chase {selBlockers[0]?.owner || 'assignee'}.</p>}
                      {sel.status === 'ready' && sel.passStatus === 'none' && <p className="text-xs text-indigo-700">Generate SiteReady Trade Pass and send link to {sel.contact}.</p>}
                      {sel.status === 'ready' && sel.passStatus === 'generated' && <p className="text-xs text-indigo-700">Send the SiteReady link to {sel.contact} via {sel.channel}.</p>}
                      {sel.status === 'link-sent' && <p className="text-xs text-indigo-700">Waiting for {sel.contact} to open the link and check in.</p>}
                      {sel.status === 'checked-in' && <p className="text-xs text-indigo-700">{sel.crewCount} crew on site. Monitor work progress and collect work proof.</p>}
                      {sel.status === 'cannot-start' && <p className="text-xs text-indigo-700">Resolve the blocker immediately so the trade can commence.</p>}
                      {(sel.status === 'signed-off' || sel.status === 'released') && <p className="text-xs text-indigo-700">Trade complete. Work proof recorded in Readiness Dossier.</p>}
                      {sel.status === 'waiting' && <p className="text-xs text-indigo-700">Waiting for upstream clearance. Earliest release: {sel.earliestRelease || 'TBD'}.</p>}
                    </div>
                  </TabsContent>

                  <TabsContent value="readiness" className="mt-4 space-y-4">
                    {selReadiness ? (
                      <>
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-slate-700">Trade Readiness Checks</p>
                          <Badge className={`text-[9px] ${selReadiness.overallStatus === 'ready' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : selReadiness.overallStatus === 'partial' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                            {selReadiness.overallStatus === 'ready' ? 'All Checks Passed' : selReadiness.overallStatus === 'partial' ? 'Partially Ready' : 'Blocked'}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          {selReadiness.checks.map(c => (
                            <div key={c.id} className={`p-3 rounded-lg border ${c.status === 'pass' ? 'bg-emerald-50/50 border-emerald-100' : c.status === 'fail' ? 'bg-red-50/50 border-red-100' : c.status === 'warning' ? 'bg-amber-50/50 border-amber-100' : 'bg-slate-50 border-slate-100'}`}>
                              <div className="flex items-center gap-2">
                                {c.status === 'pass' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : c.status === 'fail' ? <XCircle className="w-4 h-4 text-red-500" /> : c.status === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <Clock className="w-4 h-4 text-slate-400" />}
                                <div className="flex-1">
                                  <p className="text-xs font-medium text-slate-800">{c.label}</p>
                                  <p className="text-[10px] text-slate-500">{c.detail}</p>
                                </div>
                                {c.required && <Badge className="text-[8px] bg-slate-100 text-slate-600">Required</Badge>}
                              </div>
                              {c.note && <p className="text-[10px] text-slate-600 mt-1.5 ml-6 italic">{c.note}</p>}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <ShieldCheck className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                        <p className="text-xs text-slate-400">No readiness checks configured for this trade</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="pass" className="mt-4 space-y-4">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] text-slate-400 font-semibold uppercase mb-2">SiteReady Trade Pass</p>
                      {sel.siteReadyLink ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-slate-200">
                            <Smartphone className="w-4 h-4 text-violet-500" />
                            <span className="text-[10px] text-slate-700 font-mono flex-1 truncate">{sel.siteReadyLink}</span>
                            <Badge className={`text-[8px] ${sel.passStatus === 'checked-in' ? 'bg-teal-50 text-teal-700' : sel.passStatus === 'link-sent' ? 'bg-violet-50 text-violet-700' : 'bg-emerald-50 text-emerald-700'}`}>
                              {sel.passStatus === 'checked-in' ? 'Checked In' : sel.passStatus === 'link-sent' ? 'Sent' : 'Generated'}
                            </Badge>
                          </div>
                          <div className="flex gap-2">
                            <Link to={`/builder/trade-pass/${sel.id}`} className="flex-1">
                              <Button variant="outline" size="sm" className="w-full text-xs" data-testid="preview-pass-btn"><Eye className="w-3 h-3 mr-1" /> Preview Pass</Button>
                            </Link>
                            <Button variant="outline" size="sm" className="text-xs" onClick={() => setQrTrade(sel)} data-testid="qr-backup-btn"><QrCode className="w-3 h-3 mr-1" /> QR Backup</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <Smartphone className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                          <p className="text-xs text-slate-400 mb-3">No pass generated yet</p>
                          {sel.status === 'ready' && (
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => handleGeneratePass(sel.id)}>
                              <Zap className="w-3 h-3 mr-1" /> Generate Pass
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    {sel.siteReadyLink && sel.passStatus === 'generated' && (
                      <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white text-xs" onClick={() => handleSendLink(sel.id)} data-testid="send-link-drawer-btn">
                        <Send className="w-3.5 h-3.5 mr-1.5" /> Send Link to {sel.contact}
                      </Button>
                    )}
                  </TabsContent>

                  <TabsContent value="attendance" className="mt-4 space-y-4">
                    {(sel.status === 'checked-in' || sel.status === 'cannot-start') ? (
                      <div className="space-y-3">
                        <div className={`p-4 rounded-lg border ${sel.status === 'cannot-start' ? 'bg-red-50 border-red-200' : 'bg-teal-50 border-teal-200'}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sel.status === 'cannot-start' ? 'bg-red-100' : 'bg-teal-100'}`}>
                              <HardHat className={`w-5 h-5 ${sel.status === 'cannot-start' ? 'text-red-600' : 'text-teal-600'}`} />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-slate-800">{sel.crewCount || 0} crew on site</p>
                              <p className="text-[10px] text-slate-500">Checked in {sel.checkInTime} · {sel.checkInDate}</p>
                            </div>
                          </div>
                        </div>
                        {sel.status === 'checked-in' && (
                          <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs" onClick={() => handleSignOff(sel.id)} data-testid="sign-off-trade-btn">
                            <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Sign Off Trade
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Users className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                        <p className="text-xs text-slate-400">Trade not yet checked in</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="proof" className="mt-4 space-y-4">
                    {selWorkProofs.length > 0 ? (
                      <div className="space-y-2">
                        {selWorkProofs.map(wp => (
                          <div key={wp.id} className={`p-3 rounded-lg border ${wp.status === 'approved' ? 'bg-emerald-50/50 border-emerald-100' : wp.status === 'rejected' ? 'bg-red-50/50 border-red-100' : 'bg-amber-50/50 border-amber-100'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <Camera className="w-4 h-4 text-slate-500" />
                              <span className="text-xs font-medium text-slate-800 flex-1">{wp.fileName}</span>
                              <Badge className={`text-[8px] ${wp.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : wp.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{wp.status}</Badge>
                            </div>
                            <p className="text-[10px] text-slate-500">{wp.uploadedBy} · {wp.uploadDate} {wp.uploadTime}</p>
                            {wp.notes && <p className="text-[10px] text-slate-600 mt-1 italic">{wp.notes}</p>}
                            {wp.status === 'pending' && (
                              <Button size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] h-6" onClick={() => { approveWorkProof(wp.id); toast.success('Work proof approved'); }} data-testid={`approve-wp-${wp.id}`}>
                                <CheckCircle2 className="w-2.5 h-2.5 mr-1" /> Approve
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Camera className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                        <p className="text-xs text-slate-400">No work proofs uploaded yet</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                <Separator className="my-4" />
                <div className="flex gap-2 flex-wrap">
                  {sel.status === 'ready' && sel.passStatus === 'none' && (
                    <Button className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => handleGeneratePass(sel.id)}>
                      <Smartphone className="w-3.5 h-3.5 mr-1" /> Generate Pass
                    </Button>
                  )}
                  {sel.siteReadyLink && (
                    <Link to={`/builder/trade-pass/${sel.id}`}>
                      <Button variant="outline" size="sm" className="text-xs"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Open Pass</Button>
                    </Link>
                  )}
                  {sel.siteReadyLink && (
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => setQrTrade(sel)}><QrCode className="w-3.5 h-3.5 mr-1" /> QR Poster</Button>
                  )}
                  <Link to="/builder/stage-readiness"><Button variant="outline" size="sm" className="text-xs"><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Readiness</Button></Link>
                  <Link to="/builder/release-calendar"><Button variant="outline" size="sm" className="text-xs"><Calendar className="w-3.5 h-3.5 mr-1" /> Calendar</Button></Link>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* QR Poster Modal */}
        <QRPosterModal open={!!qrTrade} onClose={() => setQrTrade(null)} trade={qrTrade} project={qrTrade ? projects.find(p => p.id === qrTrade.projectId) : null} />
      </div>
    </AppLayout>
  );
}
